import React from "react";
import { useTranslation } from "react-i18next";

import Heading from "@components/layout/Heading";
import ConfirmModal from "@components/shared/ConfirmModal";
import ConfirmationDetail from "@components/shared/ConfirmationDetail";
import WorkflowComplete from "@components/shared/WorkflowComplete";
import { toastError, toastSuccess, toastWarning } from "@utils/toast";
import { isValidationError } from "@utils/formError";
import { DraftNote } from "@components/notes/NoteComposer";
import { mergeDraftNoteIntoRow } from "@components/notes/mergeDraftNoteIntoRow";
import { flushNoteStepDraft } from "@components/notes/flushNoteStepDraft";
import { DBTables } from "@utils/constants/dbTables";

type Props = {
  data: any;
  workflowInstance: any;
  isComplete: boolean;
  /**
   * Entity mutation handler. May return the new/updated subscriber id so the
   * confirm component can flush an optional NoteStep draft afterwards. If a
   * draft is present but no id is returned, the draft is silently skipped.
   */
  onSubmit: () => Promise<string | void> | void;
  onPrevious: () => void;
  onEdit: (step: number) => void;
  onCancel: () => void;
  /** Optional draft from the wizard's NoteStep; flushed after onSubmit resolves. */
  draftNote?: DraftNote;
  /** i18n key used in the flush-failure warning toast (e.g. "common.subscriber"). */
  noteArtifactNameKey?: string;
  /** DBTables value identifying the reference table for the note (e.g. DBTables.Subscribers). */
  noteReferenceTable?: DBTables;
};

const ConfirmSubscriber: React.FunctionComponent<Props> = ({
  isComplete,
  onSubmit,
  onPrevious,
  onEdit,
  onCancel,
  workflowInstance,
  data,
  draftNote,
  noteArtifactNameKey,
  noteReferenceTable,
}) => {
  const { t } = useTranslation();
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      const subscriberId = await onSubmit();

      // FR-1.1 + FR-1 invariant: flush optional NoteStep draft AFTER the entity
      // commits. Note failure does NOT roll back the entity; user gets a warning
      // toast and can re-add via the audit-trail panel.
      const noteResult =
        draftNote && noteReferenceTable && subscriberId
          ? await flushNoteStepDraft(
              draftNote,
              noteReferenceTable,
              subscriberId,
            )
          : { kind: "skipped" as const };

      if (noteResult.kind === "failed") {
        toastWarning(
          t("notes.step.flush_failed", {
            artifact: t(noteArtifactNameKey || "common.subscriber"),
          }),
        );
      } else {
        toastSuccess(t("subscriber.toast.success"));
      }
    } catch (error: any) {
      const toastText = t("subscriber.toast.failure");
      if (isValidationError(error)) {
        const { errors } = error.response.data;
        const messages = errors.map((i: any) => i.message);
        toastError(
          <>
            <p>{toastText}</p>
            <ul className="list-disc pl-4">
              {messages.map((text: any) => {
                return <li key={text}>{text}</li>;
              })}
            </ul>
          </>
        );
      } else {
        toastError(toastText);
      }
    }
    setIsLoading(false);
    setShowConfirmModal(false);
  };

  if (isComplete) {
    return (
      <>
        <WorkflowComplete references={[workflowInstance?.id]} />
      </>
    );
  }

  // Merge the draft note into the LAST data row (conventionally evidence) so
  // its data-index → step-index alignment is preserved and the Edit button
  // navigates back to the shared note+evidence step.
  const dataWithNote =
    data.length > 0
      ? [
          ...data.slice(0, -1),
          mergeDraftNoteIntoRow(data[data.length - 1], draftNote, t),
        ]
      : data;

  return (
    <>
      <div className="flex flex-col gap-4 grow">
        <div className="p-6 space-y-4">
          <Heading light>{t("subscriber.create.confirm_subscriber")}</Heading>
          <ConfirmationDetail data={dataWithNote} onEdit={onEdit} />
        </div>

        <div className="grow" />

        <footer className="flex gap-4 p-6 border-t border-gray-200">
          <button
            type="button"
            className="btn-outline-primary"
            onClick={onPrevious}
          >
            {t("common.prev_step")}
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit}>
            {t("common.ledger")}
          </button>
          <button
            type="button"
            className="btn-outline-primary"
            onClick={onCancel}
          >
            {t("common.cancel")}
          </button>
        </footer>
      </div>

      <ConfirmModal
        open={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
        }}
        onConfirm={handleConfirm}
        isSubmitting={isLoading}
      >
        {t("subscriber.modal.confirm")}
      </ConfirmModal>
    </>
  );
};

export default ConfirmSubscriber;
